/**
 * MoveObjectDialog - Dialog for moving an object to a new location
 */

import type { Component, ChangeEvent } from 'react';
import { RagsObject } from '../models/Object';
import { Character } from '../models/Character';
import Dialog from './Dialog';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const TheGame: any;
declare const Finder: any;
declare const GameCommands: any;

interface MoveObjectDialogProps {
    object: RagsObject;
    onSave: (locationType: string, locationName: string) => void;
    onCancel: () => void;
}

interface MoveObjectDialogState {
    locationType: string;
    selectedCharacter: string;
    selectedContainerGroup: string;
    selectedContainer: string;
    selectedRoom: string;
}

class MoveObjectDialog extends ReactComponent<MoveObjectDialogProps, MoveObjectDialogState> {
    constructor(props: MoveObjectDialogProps) {
        super(props);
        this.state = {
            locationType: props.object.locationtype || 'LT_NULL',
            selectedCharacter: props.object.locationtype === 'LT_CHARACTER' ? props.object.locationname : '',
            selectedContainerGroup: '',
            selectedContainer: props.object.locationtype === 'LT_IN_OBJECT' ? props.object.locationname : '',
            selectedRoom: props.object.locationtype === 'LT_ROOM' ? props.object.locationname : ''
        };
    }

    handleSave = (): void => {
        let locationName = '';
        let locationType = this.state.locationType;
        
        if (this.state.locationType === 'LT_CHARACTER') {
            locationName = this.state.selectedCharacter;
        } else if (this.state.locationType === 'LT_IN_OBJECT') {
            locationName = this.state.selectedContainer;
        } else if (this.state.locationType === 'LT_ROOM_CURRENT') {
            locationType = 'LT_ROOM';
            const CurrentRoomGuid = (window as any).CurrentRoomGuid || '00000000-0000-0000-0000-000000000001';
            locationName = CurrentRoomGuid;
        } else if (this.state.locationType === 'LT_ROOM') {
            locationName = this.state.selectedRoom;
        }
        
        this.props.onSave(locationType, locationName);
    };

    render(): any {
        const DialogComponent = (window as any).Dialog;
        if (!DialogComponent) return null;

        // Get available characters, sorted alphabetically
        const characters = (TheGame?.Characters || []).sort((a: Character, b: Character) => 
            a.Charname.localeCompare(b.Charname, undefined, { sensitivity: 'base' })
        );
        
        // Get available container objects (objects that can contain other objects), sorted alphabetically
        const containerObjects = (TheGame?.Objects || []).filter((obj: RagsObject) => 
            obj.bContainer && obj.UniqueIdentifier !== this.props.object.UniqueIdentifier
        ).sort((a: RagsObject, b: RagsObject) => 
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        
        // Group containers by group
        const containerGroups = new Map<string, RagsObject[]>();
        containerObjects.forEach((obj: RagsObject) => {
            const group = obj.GroupName || 'None';
            if (!containerGroups.has(group)) {
                containerGroups.set(group, []);
            }
            containerGroups.get(group)!.push(obj);
        });
        
        // Get available rooms, sorted alphabetically
        const rooms = (TheGame?.Rooms || []).sort((a: any, b: any) => 
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );

        return React.createElement(DialogComponent, {
            title: `Move Object: ${this.props.object.name}`,
            onClose: this.props.onCancel,
            actionButtons: React.createElement(React.Fragment, null,
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.props.onCancel
                }, 'Cancel'),
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.handleSave
                }, 'Move')
            ),
            closeOnBackdropClick: false
        },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '16px' } },
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Location Type:'),
                    React.createElement('select', {
                        value: this.state.locationType,
                        onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            this.setState({ 
                                locationType: e.target.value,
                                selectedCharacter: '',
                                selectedContainer: '',
                                selectedContainerGroup: '',
                                selectedRoom: ''
                            });
                        },
                        style: {
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            background: 'var(--main-bg-color)',
                            color: 'var(--main-text-color)'
                        }
                    },
                        React.createElement('option', { value: 'LT_NULL' }, 'No location'),
                        React.createElement('option', { value: 'LT_PLAYER' }, 'Player inventory'),
                        React.createElement('option', { value: 'LT_CHARACTER' }, 'Character inventory'),
                        React.createElement('option', { value: 'LT_IN_OBJECT' }, 'Inside another object'),
                        React.createElement('option', { value: 'LT_ROOM_CURRENT' }, 'Current room'),
                        React.createElement('option', { value: 'LT_ROOM' }, 'Any room')
                    )
                ),
                this.state.locationType === 'LT_CHARACTER' && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Character:'),
                    React.createElement('select', {
                        value: this.state.selectedCharacter,
                        onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            this.setState({ selectedCharacter: e.target.value });
                        },
                        style: {
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            background: 'var(--main-bg-color)',
                            color: 'var(--main-text-color)'
                        }
                    },
                        React.createElement('option', { value: '' }, 'Select character...'),
                        characters.map((char: Character) =>
                            React.createElement('option', { key: char.Charname, value: char.Charname }, char.Charname)
                        )
                    )
                ),
                this.state.locationType === 'LT_IN_OBJECT' && React.createElement(React.Fragment, null,
                    containerObjects.length === 0 ? React.createElement('div', { style: { color: 'var(--main-text-color)', padding: '8px' } }, 'No container objects available') :
                    containerGroups.size > 1 ? React.createElement(React.Fragment, null,
                        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                            React.createElement('label', { style: { fontWeight: 'bold' } }, 'Container Group:'),
                            React.createElement('select', {
                                value: this.state.selectedContainerGroup,
                                onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                                    this.setState({ 
                                        selectedContainerGroup: e.target.value,
                                        selectedContainer: ''
                                    });
                                },
                                style: {
                                    padding: '8px',
                                    fontFamily: 'var(--font-game-text)',
                                    fontSize: '14px',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '4px',
                                    background: 'var(--main-bg-color)',
                                    color: 'var(--main-text-color)'
                                }
                            },
                                React.createElement('option', { value: '' }, 'Select group...'),
                                Array.from(containerGroups.keys()).sort((a, b) => 
                                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                                ).map(group =>
                                    React.createElement('option', { key: group, value: group }, group)
                                )
                            )
                        ),
                        this.state.selectedContainerGroup && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                            React.createElement('label', { style: { fontWeight: 'bold' } }, 'Container Object:'),
                            React.createElement('select', {
                                value: this.state.selectedContainer,
                                onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                                    this.setState({ selectedContainer: e.target.value });
                                },
                                style: {
                                    padding: '8px',
                                    fontFamily: 'var(--font-game-text)',
                                    fontSize: '14px',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '4px',
                                    background: 'var(--main-bg-color)',
                                    color: 'var(--main-text-color)'
                                }
                            },
                                React.createElement('option', { value: '' }, 'Select object...'),
                                (containerGroups.get(this.state.selectedContainerGroup) || []).map((obj: RagsObject) =>
                                    React.createElement('option', { key: obj.UniqueIdentifier, value: obj.UniqueIdentifier }, obj.name)
                                )
                            )
                        )
                    ) : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                        React.createElement('label', { style: { fontWeight: 'bold' } }, 'Container Object:'),
                        React.createElement('select', {
                            value: this.state.selectedContainer,
                            onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                                this.setState({ selectedContainer: e.target.value });
                            },
                            style: {
                                padding: '8px',
                                fontFamily: 'var(--font-game-text)',
                                fontSize: '14px',
                                border: '1px solid rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                                background: 'var(--main-bg-color)',
                                color: 'var(--main-text-color)'
                            }
                        },
                            React.createElement('option', { value: '' }, 'Select object...'),
                            containerObjects.map((obj: RagsObject) =>
                                React.createElement('option', { key: obj.UniqueIdentifier, value: obj.UniqueIdentifier }, obj.name)
                            )
                        )
                    )
                ),
                this.state.locationType === 'LT_ROOM' && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Room:'),
                    React.createElement('select', {
                        value: this.state.selectedRoom,
                        onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            this.setState({ selectedRoom: e.target.value });
                        },
                        style: {
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            background: 'var(--main-bg-color)',
                            color: 'var(--main-text-color)'
                        }
                    },
                        React.createElement('option', { value: '' }, 'Select room...'),
                        rooms.map((room: any) =>
                            React.createElement('option', { key: room.UniqueID, value: room.UniqueID }, room.Name)
                        )
                    )
                )
            )
        );
    }
}

(window as any).MoveObjectDialog = MoveObjectDialog;
export default MoveObjectDialog;
